import { Route, RouteConfig } from 'vue-router/types/router';
import { useToast } from '@/composables/useToast';
import { BreadCrumb } from '@/types/common/breadcrumb.types';
import { HblViewTabs } from '@/types/hbl/hbl.enums';
import { getMblShipmentNumberFromHbl } from '@/modules/house-bl';
import { MBL_VIEW_TABS } from '@/constants/master-bl';
import { useAirHblStore } from '@/store/modules/air-imports/useAirHblStore';
import { AirHblEntity } from '@/types/air/hbl/air-hbl.entity';
import { PermissionsAirImportsFeatures } from '@/constants/permissions';

const breadCrumbLabel: Record<HblViewTabs, string> = {
  [HblViewTabs.Details]: '',
  [HblViewTabs.Charges]: 'Charges',
  [HblViewTabs.Milestones]: 'Milestones',
  [HblViewTabs.Attachments]: 'Attachments',
  [HblViewTabs.ItApplication]: 'In Bond',
  [HblViewTabs.Print]: 'Print',
};
const getBreadCrumbLabel = (tab: HblViewTabs): string => {
  return breadCrumbLabel[tab] || '';
};

const getFromBreadCrumb = (breadCrumb: BreadCrumb): BreadCrumb => {
  return breadCrumb || { text: `MAWB list view`, to: { name: 'AirImports' } };
};

const generateHblViewBreadCrumbs = (
  hbl: AirHblEntity,
  tab: HblViewTabs,
  fromBreadCrumb: BreadCrumb
) => {
  const resultBreadcrumbs: BreadCrumb[] = [
    fromBreadCrumb,
    {
      text: `Air MBL ${getMblShipmentNumberFromHbl(hbl.shipmentNumber)}`,
      to: {
        name: 'MasterAWB',
        params: {
          id: hbl.mblId,
        },
      },
    },
    {
      text: 'Air HBL View List',
      to: {
        name: 'MasterAWB',
        params: {
          id: hbl.mblId,
        },
        query: {
          tab: MBL_VIEW_TABS.hblList,
        },
      },
    },
    {
      text: `Air HBL ${hbl.shipmentNumber}`,
      to: { name: 'AirHouseBL', query: { tab: HblViewTabs.Details } },
    },
  ];
  if (tab !== HblViewTabs.Details && tab) {
    resultBreadcrumbs.push({ text: getBreadCrumbLabel(tab) });
  }

  return resultBreadcrumbs;
};

export const getAirHblViewBreadCrumbs = (route: Route): BreadCrumb[] => {
  const hblStore = useAirHblStore();

  if (!hblStore.hbl) return [];

  const query = route.query.tab as HblViewTabs;
  const fromBreadCrumb = route.meta!.fromBreadCrumb as BreadCrumb;
  return generateHblViewBreadCrumbs(hblStore.hbl, query, getFromBreadCrumb(fromBreadCrumb));
};

export const airHblRoutes: RouteConfig[] = [
  {
    name: 'AirHouseBL',
    meta: {
      permission: PermissionsAirImportsFeatures.HBL,
      disableWarehouseSwitch: true,
      dynamicTitle: true,
      pageTitle: 'Air HBL',
      breadCrumbs: getAirHblViewBreadCrumbs,
    },
    path: 'air-hbl/view/:id',
    props: true,
    component: () => import('@/views/air-imports/hbl/AirHblView.vue'),
  },
  {
    name: 'AirHouseBLCreate',
    meta: {
      permission: PermissionsAirImportsFeatures.HBL,
      disableWarehouseSwitch: true,
      pageTitle: 'New Air HBL',
    },
    path: 'air-hbl/create',
    component: () => import('@/views/air-imports/hbl/AirHblCreateView.vue'),
    beforeEnter: (to, from, next) => {
      if (to.query.mblId) {
        return next();
      }
      useToast().error('No MBL ID provided');
      return next({ name: 'AirImports' });
    },
  },
];
